import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { BRASILIA_TIME_ZONE } from "~/lib/datetime/brasilia";
import { en } from "./messages/en";
import { es } from "./messages/es";
import { type MessageCatalog, type MessageKey, ptBR } from "./messages/pt-BR";

export type Locale = "pt-BR" | "es" | "en";

export const LOCALES: readonly Locale[] = ["pt-BR", "es", "en"];
export const DEFAULT_LOCALE: Locale = "pt-BR";

export const catalogs: Record<Locale, MessageCatalog> = {
  "pt-BR": ptBR,
  es,
  en,
};

export const localeLabels: Record<Locale, string> = {
  "pt-BR": "Português (BR)",
  es: "Español",
  en: "English",
};

export const localeShortLabels: Record<Locale, string> = {
  "pt-BR": "PT",
  es: "ES",
  en: "EN",
};

export type MessageParams = Record<string, string | number>;

const PLACEHOLDER = /\{(\w+)\}(\.)?/g;
const ENDS_A_SENTENCE = /[.!?…]$/;

/**
 * Deliberately tiny formatter: named `{placeholder}` interpolation only. Any
 * placeholder without a matching parameter is left verbatim so a missing value
 * is loud in the UI rather than silently rendering "undefined". A template
 * period right after a value that already ends a sentence is dropped, so a
 * server message such as "… indisponível." never renders as "indisponível..".
 */
export function formatMessage(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(PLACEHOLDER, (match, name: string, period?: string) => {
    const value = params[name];
    if (value === undefined) return match;
    const text = String(value);
    return period && !ENDS_A_SENTENCE.test(text.trimEnd()) ? `${text}${period}` : text;
  });
}

export type Translate = (key: MessageKey, params?: MessageParams) => string;

export function tPlural(
  translate: Translate,
  key: string,
  count: number,
  params?: MessageParams,
): string {
  const suffix = count === 1 ? "one" : "other";
  return translate(`${key}.${suffix}` as MessageKey, { count, ...params });
}

export type I18nValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translate;
  tPlural: (key: string, count: number, params?: MessageParams) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatPercent: (value: number, fractionDigits?: number) => string;
  formatCurrency: (value: number | string | null, currency?: string) => string;
  formatDate: (value: string | Date, options?: Intl.DateTimeFormatOptions) => string;
  formatDateTime: (value: string | Date, options?: Intl.DateTimeFormatOptions) => string;
  formatRelative: (value: string | Date, now?: Date) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

const LOCALE_STORAGE_KEY = "backchannel.tax.locale";

/**
 * The locale preference is a UI setting with no security value, so it is the
 * one thing we do persist. Session state and tokens never are — see
 * `src/lib/auth/token-store.ts`.
 */
function readStoredLocale(): Locale | null {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return LOCALES.includes(stored as Locale) ? (stored as Locale) : null;
  } catch {
    return null;
  }
}

function detectLocale(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  const stored = readStoredLocale();
  if (stored) return stored;
  // Match preferred browser languages in order, including regional variants.
  // Portuguese remains the source language and the unsupported-language fallback.
  const languages = window.navigator.languages.length
    ? window.navigator.languages
    : [window.navigator.language];
  for (const language of languages) {
    const base = language.toLowerCase().split("-")[0];
    if (base === "pt") return "pt-BR";
    if (base === "es" || base === "en") return base;
  }
  return DEFAULT_LOCALE;
}

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 3600_000],
  ["month", 30 * 24 * 3600_000],
  ["day", 24 * 3600_000],
  ["hour", 3600_000],
  ["minute", 60_000],
  ["second", 1000],
];

/** Four-digit year. `dateStyle: "short"` would print `18/8/26` in es and `8/18/26` in en. */
const DATE_DEFAULTS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
};

/**
 * Callers that ask for `dateStyle: "short"` (with or without `timeStyle:
 * "short"`) get the same four-digit date: a fiscal tool never shows `5/10/26`.
 */
export function withFourDigitYear(
  options: Intl.DateTimeFormatOptions | undefined,
): Intl.DateTimeFormatOptions {
  if (!options) return DATE_DEFAULTS;
  if (options.dateStyle !== "short") return options;
  const { dateStyle: _dateStyle, timeStyle, ...rest } = options;
  return {
    ...rest,
    ...DATE_DEFAULTS,
    ...(timeStyle === "short" ? { hour: "2-digit", minute: "2-digit" } : {}),
    ...(timeStyle && timeStyle !== "short"
      ? { hour: "2-digit", minute: "2-digit", second: "2-digit" }
      : {}),
  };
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** True for calendar dates ("2026-03-01") that must not shift across zones. */
export function isDateOnlyValue(value: string | Date): boolean {
  return typeof value === "string" && DATE_ONLY.test(value);
}

/**
 * Operator-facing instants render in Brasília. Callers may override `timeZone`.
 * Date-only strings stay zone-free so local-calendar parsing does not shift days.
 */
export function withOperatorTimeZone(
  value: string | Date,
  options: Intl.DateTimeFormatOptions | undefined,
): Intl.DateTimeFormatOptions {
  if (options?.timeZone !== undefined) return options;
  if (isDateOnlyValue(value)) return options ?? {};
  return { timeZone: BRASILIA_TIME_ZONE, ...options };
}

/** Pure date formatter used by the provider and unit tests. */
export function formatLocaleDate(
  locale: Locale,
  value: string | Date,
  options?: Intl.DateTimeFormatOptions,
): string {
  const resolved = withOperatorTimeZone(value, withFourDigitYear(options));
  return new Intl.DateTimeFormat(locale, resolved).format(
    typeof value === "string" ? parseDateValue(value) : value,
  );
}

/** Pure date-time formatter used by the provider and unit tests. */
export function formatLocaleDateTime(
  locale: Locale,
  value: string | Date,
  options?: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: BRASILIA_TIME_ZONE,
    ...DATE_DEFAULTS,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    ...options,
  }).format(typeof value === "string" ? parseDateValue(value) : value);
}

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? detectLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
    // Keep browser chrome aligned with the selected catalogue as well as the
    // visible UI. The static HTML title remains the Portuguese fallback for
    // the brief period before React mounts.
    document.title = `${catalogs[locale]["app.brand"]} · ${catalogs[locale]["app.name"]}`;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // Private mode or blocked storage: the in-memory preference still holds.
    }
  }, []);

  const value = useMemo<I18nValue>(() => {
    const catalog = catalogs[locale];

    const t: Translate = (key, params) => formatMessage(catalog[key] ?? key, params);

    const formatNumber = (n: number, options?: Intl.NumberFormatOptions) =>
      new Intl.NumberFormat(locale, options).format(n);

    return {
      locale,
      setLocale,
      t,
      tPlural: (key, count, params) => tPlural(t, key, count, params),
      formatNumber,
      formatPercent: (n, fractionDigits = 2) =>
        new Intl.NumberFormat(locale, {
          style: "percent",
          minimumFractionDigits: 0,
          maximumFractionDigits: fractionDigits,
        }).format(n),
      formatCurrency: (n, currency = "BRL") =>
        n === null
          ? t("common.costPending")
          : new Intl.NumberFormat(locale, {
              style: "currency",
              currency,
              // "R$" in every language: Spanish CLDR would otherwise spell the code ("12,50 BRL").
              currencyDisplay: "narrowSymbol",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(typeof n === "string" ? Number.parseFloat(n) : n),
      formatDate: (value, options) => formatLocaleDate(locale, value, options),
      formatDateTime: (value, options) => formatLocaleDateTime(locale, value, options),
      formatRelative: (value, now = new Date()) => {
        const date = typeof value === "string" ? parseDateValue(value) : value;
        const deltaMs = date.getTime() - now.getTime();
        const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
        for (const [unit, ms] of RELATIVE_UNITS) {
          if (Math.abs(deltaMs) >= ms || unit === "second") {
            return formatter.format(Math.round(deltaMs / ms), unit);
          }
        }
        return formatter.format(0, "second");
      },
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Date-only strings ("2026-03-01") are parsed as local, not UTC midnight. */
function parseDateValue(value: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
  }
  return new Date(value);
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return value;
}

export function useT(): Translate {
  return useI18n().t;
}

export type { MessageCatalog, MessageKey };
